const SDS_API = 'https://sos-s02.gistda.or.th/';
const AUTH_KEY = 'gCeRcfFdJcZ2wxgm3QmuaKqImJft8u3u';
const CLIENT_SERVICE = 'frontend-client';

import * as http from "@/common/http";
import { sortObjectArray } from "@/common/utils";
import { transformDate } from "@/common/filters";

const sha512 = require("js-sha512");

interface IAuth {
  UserID: number;
  Token: string;
}

interface ISource {
  SourceID: number;
  SourceName: string;
}

interface IStation {
  StationID?: number;
  StationLabel?: string;
  StationName?: string;
  Latitude?: number;
  Longitude?: number;
  GeoJSON?: any;
  SourceID?: number;
  SourceName?: string;
  // Available measures and observed data indexed with MeasureType
  // Assigned every time when fetching aggregate from server
  Measures?: {[key:string] : IMeasure};
  DataInterval?: "Hourly" | "Daily";
}

/**
 * Aggregate[] contains observed values of a MeasureType
 * It is always sorted from StartDatetime to EndDatetime ascending
 * The time interval is either hourly or daily
 */
interface IAggregate {
  /*SourceName?: string;
  StationName?: string;
  Latitude?: number;
  Longitude?: number;*/
  StartDatetime?: string;
  EndDatetime?: string;
  MeasureType?: string;
  MeasureUnit?: string;
  AvgValue?: number;
  MinValue?: number;
  MaxValue?: number;
}

// struct should following chartjs definition
// keys are case senstive
interface IChartSeries {
  name: string;
  data: {[key:string] : number };
  color?: string;
}

interface IMeasure {
  Definition: {[key:string] : any};
  Data: IAggregate[];
  Min: number; // Min of all values
  Max: number; // Max of all values;
  Series?: IChartSeries[]; // contains Min/Max/Avg observed value of a MeasureType
  XTitle?: string;
  YTitle?: string;
  Visible?:boolean;  // used to control visibility of chart
}

interface IObserves {
  Station?: IStation;
  Interval?: "Hourly" | "Daily";
  // Observed data indexed with MeasureType
  // Assigned in fetching aggregate
  Measures: {[key:string] : IMeasure }; 
}

export class SDS {
  Auth: IAuth;
  // Three sources:
  Sources: ISource[];
  // All stations indexed with SourceID
  Stations: {[key:string] : IStation[] };
  // All measures indexed with SourceID
  MeasureGroups: {[key:string] : any[]} = {};

  // Array of available measures of a station at the selected time
  // It is used as v-select items
  Measures: {[key:string] : any}[] = [];
  // Array of MeasureType selected from Measures via v-select v-model
  SelectedMeasures: string[] = [];
  // The Station of interesting.
  // Assigned before call fetch aggregate, suc has in fetchStationImage
  protected SelectedStation: IStation;

  // Followings should be updated in every fetch aggregate
  
  Charts:IMeasure[] = [];
  ObservedTimeMin: number;
  ObservedTimeMax: number;
  ObservedTimeRange: number[];

  fetching:boolean = false;

  get SelectedStationID() : number {
    return this.SelectedStation && this.SelectedStation.StationID;
  }

  setSelectedStation(SourceID:number, StationID:number) {
    this.SelectedStation = this.getStationByID(SourceID, StationID);
  }

  get SelectAllIcon() : string {
    let icon = 'mdi-checkbox-blank-outline';
    if (this.SelectedMeasures.length==this.Measures.length) {
      icon = 'mdi-close-box';
    } else if (this.SelectedMeasures.length>0) {
      icon = 'mdi-minus-box';
    }
    return icon;
  }

  get AllSelected() : boolean {
    return this.SelectedMeasures && this.Measures && 
           this.SelectedMeasures.length>1 && this.SelectedMeasures.length==this.Measures.length;
  }
  
  get OneSelected() : boolean {
    return this.SelectedMeasures && this.Measures && this.SelectedMeasures.length==1;
  }
  
  get MultipleSelected() : boolean {
    return this.SelectedMeasures && this.Measures && 
           this.SelectedMeasures.length>1 && this.SelectedMeasures.length<this.Measures.length;
  }

  get ObservedTimeStart() : Date {
    if (this.ObservedTimeRange && this.ObservedTimeRange.length==2) {
      return new Date(this.ObservedTimeRange[0]);
    } else {
      return new Date();
    }
  }
  
  get ObservedTimeEnd() : Date {
    if (this.ObservedTimeRange && this.ObservedTimeRange.length==2) {
      return new Date(this.ObservedTimeRange[1]);
    } else {
      return this.ObservedTimeStart;
    }
  }

  get TimeStep() : number {
    return 1;
    //return this.AggregateMode=='Hourly' ? (60 * 60) : (60 * 60 * 24);
  }

  get SelectionPlaceholder() : string | string[] {
    return (this.Measures && this.Measures.length>0) ?
           ['please','^select$'] : 'sNoDataFound';
  }

  get InASingleYear() : boolean {
    let is:boolean = false;
    if (this.ObservedTimeMin!==undefined && this.ObservedTimeMax!==undefined) {
      let st = (new Date(this.ObservedTimeMin)).getFullYear();
      let et = (new Date(this.ObservedTimeMax)).getFullYear();
      is = (st==et);
    }
    return is;
  }

  get InASingleDate() : boolean {
    let is:boolean = false;
    if (this.ObservedTimeMin!==undefined && this.ObservedTimeMax!==undefined) {
      let st = (new Date(this.ObservedTimeMin)).toISOStringWithTimeZone().split('T')[0];
      let et = (new Date(this.ObservedTimeMax)).toISOStringWithTimeZone().split('T')[0];
      is = (st==et);
    }
    return is;
  }

  get IsHourly() : boolean {
    return this.SelectedStation && this.SelectedStation.DataInterval == 'Hourly';
  }
  
  get IsDaily() : boolean {
    return this.SelectedStation && this.SelectedStation.DataInterval == 'Daily';
  }

  get TimeFormat() : string {
    return this.InASingleDate ? 'hh:mm:ss' : this.IsHourly ? 'yyyy-MM-dd hh:mm' : 'yyyy MMM dd';
  }

  get NoDataFound() : boolean {
    return this.ObservedTimeMin===undefined || this.ObservedTimeMax===undefined;
  }

  get NoDataSelected() : boolean {
    return this.ObservedTimeMin!==undefined && this.ObservedTimeMax!==undefined && 
           (this.ObservedTimeRange && this.ObservedTimeRange.length==2) &&
           !(this.SelectedMeasures && this.SelectedMeasures.length>0);
  }

  get ShowTimeRange() : boolean {
    return this.ObservedTimeMin!==undefined && this.ObservedTimeMax!==undefined && 
           (this.ObservedTimeRange && this.ObservedTimeRange.length==2) &&
           (this.SelectedMeasures && this.SelectedMeasures.length>0);
  }

  toggleSelectAllMeasures(event) {
    if (this.SelectedMeasures.length==this.Measures.length) {
      this.SelectedMeasures = [];
    } else {
      this.SelectedMeasures = this.Measures.map((mg) => {
        return mg.MeasureType;
      });
    }
  }

  /**
   * STEP 1
   * First of all, user must login in order to get this.Auth available
   * @param userId 
   * @param password 
   */
  async login(userId:string, password:string) : Promise<boolean> {
    let url = SDS_API + 'mentor/users/login';
    try {
      let data = await http.request(url, {
        method: 'POST',
        headers: {
          'Client-Service' : CLIENT_SERVICE,
          'Auth-Key': AUTH_KEY
        },
        body: JSON.stringify({ username:userId, password:sha512(password) })
      });
      if (data && data.status==200) {
        this.Auth = {
          UserID: data.id,
          Token: data.token
        };
        return true;
      }
      return false;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Every fetch data from server finally requests from and responses back here
   * @param url 
   * @param method 
   */
  private async requestData(url:string, method:string) : Promise<{[key:string] : any}> {
    try {
      if (!this.Auth) {
        throw {
          status: 401,
          message: 'Unauthorized.'
        };
      }
      let data = await http.request(url, {
        method: method,
        headers: {
          'Client-Service' : CLIENT_SERVICE,
          'Auth-Key': AUTH_KEY,
          'User-ID':	this.Auth.UserID + '',
          Authorization: this.Auth.Token
        }
      });
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * STEP 2
   * Right after successful login, do this in order to get sources and stations
   */
  async fetchData() : Promise<boolean> {
    try {
      let sources = await this.fetchSources();
      if (sources && sources.length>0) {
        for (let i=0; i<sources.length; i++) {
          await this.fetchStations(sources[i].SourceID);
        };
        this.fetchAllMeasures();
      }
      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * STEP 2.1
   * Get all resources before others
   */
  async fetchSources() : Promise<ISource[]> {
    try {
      let url = SDS_API + 'sds/sensor/sources';
      let data = await this.requestData(url, 'GET');
      if (data && data.data) {
        this.Sources = data.data.map((s) => {
          return {
            SourceID: s.SourceID,
            SourceName: s.SourceName
          }
        });
      }
      return this.Sources;
    } catch (error) {
      throw error;
    }
  }

  /**
   * STEP 2.2:
   * Get all stations right after STEP 2.1 and store them
   * in Stations by SourceID
   * @param SourceID 
   */
  async fetchStations(SourceID:number) : Promise<IStation[]> {
    try {
      let url = SDS_API + 'sds/sensor/stations?SourceID=' + SourceID;
      let data = await this.requestData(url, 'GET');
      this.Stations = this.Stations || {};
      if (data && data.data) {
        this.Stations[SourceID+''] = data.data;
      } else {
        this.Stations[SourceID+''] = [];
      }
      return this.Stations[SourceID+''];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Run this when click a station on the map
   * @param SourceID 
   * @param StationID
   * @param ObservedDate
   */
  async fetchStationImage(SourceID:number, StationID:number, ObservedDate?:Date) : Promise<any> {
    try {
      ObservedDate = ObservedDate || (new Date());
      let odt: string = ObservedDate.toISOStringWithTimeZone().replace('T',' ');
      let url = SDS_API + 'sds/sensor/observedimages' + 
                '?SourceID=' + SourceID + '&StationID=' + StationID + '&ObservedDatetime='+ odt;
      
      this.SelectedStation = this.getStationByID(SourceID, StationID);
      let data = await this.requestData(url, 'GET');
      if (data && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Run this, if none exists in this.MeasureGroups, the first in when fetching an aggregate
   * We wont to fetch it again for the same source
   * *****
   * There are a lot of groups, so need to fine tune what groups are needed
   * @param SourceID 
   */
  async fetchMeasureGroups(SourceID:number) : Promise<any> {
    try {
      let url = SDS_API + 'sds/sensor/measuregroups?SourceID=' + SourceID;
      let data = await this.requestData(url, 'GET');
      this.MeasureGroups = this.MeasureGroups || {};
      if (data && data.data) {
        let key = SourceID+'';
        this.MeasureGroups[key] = [];
        let n = 0;
        data.data.forEach((d) => {
          if (d.MeasureGroupDisplay==1) {
            d.DisplayName += d.ImageType ? (' (' + d.ImageType + ')') : 
                             d.MeasureUnit ? (' (' + d.MeasureUnit + ')') : '';
            d.DisplayText = 'ID' + d.MeasureGroupID + '_' + d.DisplayName;
            if (d.ValueType) {
              d.DisplayText + ' / ' + d.ValueType;
            }
            n++;
            d.DisplayText2 = ('0' + n).slice(-2) + ' : ' + d.DisplayName;
            d.DisplayText2 += d.ValueType ? (' / ' + d.ValueType) : '';
            this.MeasureGroups[key].push(d);
          }
        });
        return this.MeasureGroups;
      } else {
        this.MeasureGroups[SourceID+''] = []
      }
      return null;
    } catch (error) {
      throw error
    }
  }

  /**
   * We dont use this directly. Instead, we use the MeasureType from aggregate
   * to get it from the cached MeasureGroups that has some groups in Thai
   * @param StationID 
   */
  async fetchMeasures(StationID:number) : Promise<any> {
    try {
      let url = SDS_API + 'sds/sensor/measures?StationID=' + StationID;
      let data = await this.requestData(url, 'GET');
      this.Measures = [];
      if (data) {
        data.forEach((d) => {
          d.MeasureType = d.MeasureGroupID + '_' + d.MeasureType;
          this.Measures.push(d);
        });
        return this.Measures;
      }
      return null;
    } catch (error) {
      throw error
    }
  }

  async fetchAllMeasures() {
    try {
      let url11 = SDS_API + 'sds/sensor/measuregroups?SourceID=1';
      let url12 = SDS_API + 'sds/sensor/measuregroups?SourceID=2';
      let url13 = SDS_API + 'sds/sensor/measuregroups?SourceID=3';
      let url2 = SDS_API + 'sds/sensor/measures';
      let all = await Promise.all([
        this.requestData(url11,'GET'),
        this.requestData(url12,'GET'),
        this.requestData(url13,'GET')
        //this.requestData(url2,'GET')
      ]);
      this.MeasureGroups = {};
      all.forEach((mg,index) => {
        let key = (index+1) + '';
        this.MeasureGroups[key] = this.MeasureGroups[key] || [];
        let n = 0;
        mg.data.forEach((d) => {
          if (d.MeasureGroupDisplay==1) {
            d.DisplayName += d.ImageType ? (' (' + d.ImageType + ')') : 
                             d.MeasureUnit ? (' (' + d.MeasureUnit + ')') : '';
            d.DisplayText = 'ID' + d.MeasureGroupID + '_' + d.DisplayName;
            if (d.ValueType) {
              d.DisplayText + ' / ' + d.ValueType;
            }
            n++;
            d.DisplayText2 = ('0' + n).slice(-2) + ' : ' + d.DisplayName;
            d.DisplayText2 += d.ValueType ? (' / ' + d.ValueType) : '';
            this.MeasureGroups[key].push(d);
          }
        });
      });
    } catch (error) {
    }
  }

  /**
   * Must run STEP 2.1 and STEP 2.2 first, so we can call this when fetch aggregate
   * @param SourceID 
   * @param StationName 
   */
  private getStationByName(SourceID:number, StationName:string) : IStation {
    let s:IStation;
    let stations:IStation[] = this.Stations[SourceID+''] || [];
    for (let i=0; i<stations.length; i++) {
      if (stations[i].StationName==StationName) {
        s = stations[i];
        break;
      }
    }
    return s;
  }

  private getStationByID(SourceID:number, StationID:number) : IStation {
    let s:IStation;
    let stations:IStation[] = this.Stations[SourceID+''] || [];
    for (let i=0; i<stations.length; i++) {
      if (stations[i].StationID==StationID) {
        s = stations[i];
        break;
      }
    }
    return s;
  }

  /**
   * Format the measures from aggregate by StationID and MeasureType
   * The measures are used for making charts
   * @param SourceID 
   * @param data 
   */
  private buildMeasure(SourceID:number, data:{[key:string] : any}) {
    let minStart:Date, maxEnd:Date
    let StartDatetime: "StartDatetime" | "StartDate";
    
    if (data) {
      let measureLists = [];
      let measures:{[key:string] : IMeasure } = {};
      data.forEach((d) => {
        let mt = d.MeasureType;
        let start,end;
        let measure:IMeasure = measures[mt];
        if (!measure) {
          let mg = this.MeasureGroups[SourceID+''];
          for (let i=0; i<mg.length; i++) {
            if (mg[i].MeasureType==mt) {
              measure = {
                Definition: mg[i],
                Data: [],
                Min: Number.MAX_VALUE,
                Max: Number.MIN_VALUE,
                Series: [{
                    name:'MAX', data:{}
                  },{
                    name:'AVG', data:{}
                  },{
                    name:'MIN', data:{}
                  }],
                XTitle: mg[i].DisplayName,
                YTitle: mg[i].MeasureUnit,
                Visible: true
              }
              measureLists.push(mg[i]);
              break;
            }
          }
        }
        if (measure) {
          measures[mt] = measure;
          if (d.StartDatetime!==undefined) {
            StartDatetime = "StartDatetime";
            start = new Date(d.StartDatetime);
            end = new Date(d.EndDatetime);
          } else if (d.StartDate!==undefined) {
            // From Aggregate of Today/Yesterday/Last30Days/LastOneYear
            StartDatetime = "StartDate";
            start = new Date(d.StartDate);
            end = new Date(d.EndDate);
          }
          if (minStart===undefined) {
            minStart = start;
          } else {
            if (minStart.getTime() > start.getTime()) {
              minStart = start;
            }
          }
          if (maxEnd===undefined) {
            maxEnd = end;
          } else {
            if (maxEnd.getTime() < end.getTime()) {
              maxEnd = end;
            }
          }
          measure.Min = Math.min(measure.Min,d.MinValue);
          measure.Max = Math.max(measure.Max,d.MaxValue);
          measure.Data.push ({
            StartDatetime: start,
            EndDatetime: end,
            MeasureUnit: d.MeasureUnit,
            AvgValue: d.AvgValue,
            MinValue: d.MinValue,
            MaxValue: d.MaxValue
          });
          measure.Series[0].data[d[StartDatetime]] = d.MaxValue;
          measure.Series[1].data[d[StartDatetime]] = d.AvgValue;
          measure.Series[2].data[d[StartDatetime]] = d.MinValue;
        }
      });
      this.Measures = measureLists;
      sortObjectArray(this.Measures,'MeasureGroupID');
      this.Charts = [];
      this.SelectedMeasures = this.Measures.map((m) => {
        this.Charts.push(measures[m.MeasureType]);
        return m.MeasureType;
      });
      this.ObservedTimeMin = minStart.getTime();
      this.ObservedTimeMax = maxEnd.getTime();
      this.ObservedTimeRange = [this.ObservedTimeMin,this.ObservedTimeMax];
      this.updateChartData();
    } else {
      this.ObservedTimeMin = undefined;
      this.ObservedTimeMax = undefined;
      this.ObservedTimeRange = undefined;
      this.Charts = [];
    }
  }

  async fetchAggregate(SourceID:number, url:string) : Promise<any> {
    try {
      this.fetching = true;
      if (!this.MeasureGroups[SourceID+'']) {
        await this.fetchMeasureGroups(SourceID);
      }
      let data = await this.requestData(url, 'GET');
      this.buildMeasure(SourceID, data.data);
      this.fetching = false;
      return this.SelectedStation.Measures;
    } catch (error) {
      let e = error;
      this.fetching = false;
      throw error
    }
  }

  async fetchHourlyAggregate(SourceID:number, StationName:string,
    StartTime:Date,EndTime:Date) : Promise<any> {
    try {
      if (!this.SelectedStation) {
        this.SelectedStation = this.getStationByName(SourceID, StationName);
      }
      let st, et, url;
      st = StartTime.toISOStringWithTimeZone();
      et = EndTime.toISOStringWithTimeZone();
      st = st.replace('T',' ');
      et = et.replace('T',' ');
      url = SDS_API + 'sds/aggregate/hourly?' +
            'SourceID=' + SourceID + '&StationName=' + StationName +
            '&StartDatetime=' + st + '&EndDatetime=' + et;
      return this.fetchAggregate(SourceID, url);
    } catch (error) {
      let e = error;
      this.fetching = false;
      throw error
    }
  }

  async fetchDailyAggregate(SourceID:number, StationName:string,
    StartTime:Date,EndTime:Date) : Promise<any> {
    try {
      if (!this.SelectedStation) {
        this.SelectedStation = this.getStationByName(SourceID, StationName);
      }
      let st, et, url;
      st = StartTime.toISOStringWithTimeZone();
      et = EndTime.toISOStringWithTimeZone();
      st = st.split('T')[0];
      et = et.split('T')[0];
      if (st!=et) {
        url = SDS_API + 'sds/aggregate/daily?' +
              'SourceID=' + SourceID + '&StationName=' + StationName +
              '&StartDate=' + st + '&EndDate=' + et;
      return this.fetchAggregate(SourceID, url);
      } else {
        st += ' 00:00:00';
        et += ' 59:59:59';
        url = SDS_API + 'sds/aggregate/hourly?' +
            'SourceID=' + SourceID + '&StationName=' + StationName +
            '&StartDatetime=' + st + '&EndDatetime=' + et;
        return this.fetchAggregate(SourceID, url);
      }
    } catch (error) {
      this.fetching = false;
      throw error
    }
  }

  async fetchDailyAggregateChunk(SourceID:number, StationName: string, DateRanges:Date[][]) : Promise<any> {
    try {
      this.fetching = true;
      /*for (let i=0; i<DateRanges.length; i++) {
        this.fetchDailyAggregate(SourceID, StationName, DateRanges[i][0], DateRanges[i][1]);
      }*/
      if (!this.SelectedStation) {
        this.SelectedStation = this.getStationByName(SourceID, StationName);
      }
      if (!this.MeasureGroups[SourceID+'']) {
        await this.fetchMeasureGroups(SourceID);
      }
      let chunks = DateRanges.map((range) => {
        let st, et, url;
        st = range[0].toISOStringWithTimeZone();
        et = range[1].toISOStringWithTimeZone();
        st = st.split('T')[0];
        et = et.split('T')[0];
        if (st==et) {
          st += ' 00:00:00';
          et += ' 59:59:59';
          url = SDS_API + 'sds/aggregate/hourly?' +
                'SourceID=' + SourceID + '&StationName=' + StationName +
                '&StartDatetime=' + st + '&EndDatetime=' + et;
        } else {
          url = SDS_API + 'sds/aggregate/daily?' +
                'SourceID=' + SourceID + '&StationName=' + StationName +
                '&StartDate=' + st + '&EndDate=' + et;
        }
        return (this.requestData(url, 'GET'));
      });
      let all = await Promise.all(chunks);
      let data = [];
      all.forEach((d) => {
        data = data.concat(d.data);
      });
      this.buildMeasure(SourceID, data);
      this.fetching = false;
      return this.SelectedStation.Measures;
    } catch (error) {
      this.fetching = false;
      console.log(error);
      throw error;
    }
  }

  async fetchAggregateOfToday(SourceID:number) : Promise<any> {
    try {
      let url;
      url = SDS_API + 'sds/aggregate/today?SourceID=' + SourceID;
      return this.fetchAggregate(SourceID, url);
    } catch (error) {
      this.fetching = false;
      throw error
    }
  }
  
  async fetchAggregateOfYesterday(SourceID:number) : Promise<any> {
    try {
      let url;
      url = SDS_API + 'sds/aggregate/yesterday?SourceID=' + SourceID;
      return this.fetchAggregate(SourceID, url);
    } catch (error) {
      this.fetching = false;
      throw error
    }
  }

  async fetchAggregateOfLast30Days(SourceID:number) : Promise<any> {
    try {
      let url = SDS_API + 'sds/aggregate/last30d?SourceID=' + SourceID;
      return this.fetchAggregate(SourceID, url);
    } catch (error) {
      this.fetching = false;
      throw error
    }
  }
  
  async fetchAggregateOfLastOneYear(SourceID:number) : Promise<any> {
    try {
      let url = SDS_API + 'sds/aggregate/last1y?SourceID=' + SourceID;
      return this.fetchAggregate(SourceID, url);
    } catch (error) {
      this.fetching = false;
      throw error
    }
  }

  /**
   * SelectedMeasures have been changed
   */
  showHideChart() {
    this.Charts.forEach((chart) => {
      chart.Visible = (this.SelectedMeasures.indexOf(chart.Definition.MeasureType)>=0);
    });
  }

  /**
   * Observed Time has changed
   */
  updateChartData() {
    let start = this.ObservedTimeRange[0];
    let end = this.ObservedTimeRange[1];
    this.Charts.forEach((chart) => {
      let max={}, avg={}, min={};
      chart.Data.forEach((d) => {
        let dtt = new Date(d.StartDatetime).getTime();
        if (dtt>=start && dtt<=end) {
          max[d.StartDatetime] = d.MaxValue;
          avg[d.StartDatetime] = d.AvgValue;
          min[d.StartDatetime] = d.MinValue;
        }
      });
      // IMPORTANT: must recreate Series
      // Otherwise, chart wont be refreshed
      chart.Series = [{
          name:'MAX', data:max
        },{
          name:'AVG', data:avg
        },{
          name:'MIN', data:min
        }
      ];
    });
  }
}